import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orderAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Package, ArrowLeft, Truck } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-gold-500/12 text-gold-700 border-gold-500/25',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  shipped: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  delivered: 'bg-green-50 text-green-700 border-green-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

function OrdersPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const res = await orderAPI.getMyOrders();
        setOrders(Array.isArray(res?.orders) ? res.orders : []);
      } catch (err) {
        setError(err.message || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, isLoading]);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-cream-100">
      <div className="bg-navy-800 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow !text-gold-400">Your Account</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold">Order History</h1>
          <p className="mt-2 text-white/60">Track your purchases and their delivery status.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/account" className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-gold-600 mb-6" data-testid="orders-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        {!isAuthenticated && !isLoading ? (
          <div className="card-classy p-10 text-center" data-testid="orders-login-required">
            <p className="text-navy-500 mb-4">Please sign in to view your orders.</p>
            <Link to="/login" className="btn-gold">Sign In</Link>
          </div>
        ) : loading ? (
          <div className="card-classy p-10 text-center text-navy-400">Loading your orders…</div>
        ) : error ? (
          <div className="card-classy p-10 text-center text-red-600" data-testid="orders-error">{error}</div>
        ) : orders.length === 0 ? (
          <div className="card-classy p-12 text-center" data-testid="orders-empty">
            <div className="w-16 h-16 mx-auto rounded-full bg-gold-500/10 flex items-center justify-center mb-4">
              <Package className="w-7 h-7 text-gold-600" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-2xl font-semibold text-navy-800 mb-2">No orders yet</h3>
            <p className="text-navy-500 mb-5">When you purchase artwork, it will appear here.</p>
            <Link to="/paintings" className="btn-gold">Browse Paintings</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="card-classy p-5 sm:p-6" data-testid={`order-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-navy-400">{o.order_number || 'Order'}</p>
                    <h3 className="font-serif text-xl font-semibold text-navy-800 mt-1">{o.artwork_title || 'Artwork'}</h3>
                    <p className="text-sm text-navy-500 mt-0.5">by {o.artist_name || 'Artist'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-xl font-semibold text-navy-800">₹{Number(o.price || 0).toLocaleString('en-IN')}</p>
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${STATUS_STYLES[o.status] || STATUS_STYLES.pending}`}>
                      {o.status || 'pending'}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-3 text-sm text-navy-500">
                  <div>
                    <span className="text-navy-400">Ordered:</span>{' '}
                    {o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </div>
                  {o.awb_number && (
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-gold-600" strokeWidth={1.6} />
                      <span>AWB: {o.awb_number}</span>
                      {o.tracking_url && (
                        <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-gold-600 underline">Track</a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrdersPage;
